import React from 'react';
import { Badge } from '@americanexpress/dls-react';
import { useLoungeById } from '@/hooks/loungesHooks/loungesHelperHooks';
import { useCommonContent } from '@/hooks/oneCmsIntlHooks/oneCmsIntlHelperHooks';
import {
  enrollmentStatuses,
  usePriorityPassEnrollmentDetails,
} from '@/hooks/priorityPassHooks/priorityPassHelperHooks';
import { cn, isPriorityPassLounge } from '@/helpers/utils';
import { LoungeTypes } from '@/constants';

type BadgeType = 'centurionNetwork' | 'priorityPass';

type LoungeIdProps = {
  loungeId: string;
  badgeType?: never;
};

type BadgeTypeProps = {
  loungeId?: undefined;
  badgeType: BadgeType;
};

type LoungeTypeBadgeProps = (LoungeIdProps | BadgeTypeProps) & {
  className?: string;
};

const LoungeTypeBadge: React.FC<LoungeTypeBadgeProps> = ({
  loungeId,
  badgeType,
  className,
}) => {
  const { access_rules, experience_type } = useLoungeById(loungeId) ?? {};
  const {
    results: { centurionNetworkLabel },
  } = useCommonContent();
  const {
    enabled: isPriorityPassEnabled,
    status: enrollmentStatus,
    badge: priorityPassBadgeStatus,
    content: priorityPassContent,
  } = usePriorityPassEnrollmentDetails();
  const isPriorityPass =
    badgeType === 'priorityPass' ||
    (isPriorityPassEnabled && isPriorityPassLounge(access_rules));
  const isCenturionNetworkLounge = [
    LoungeTypes.CENTURION,
    LoungeTypes.SIDECAR,
  ].includes(experience_type);
  const shouldRenderCenturionNetworkBadge =
    badgeType === 'centurionNetwork' || isCenturionNetworkLounge;

  const badgeClassNames = cn([
    className,
    'tw-body-xs-bold tw-absolute tw-top-0 tw-z-10 first-child_tw-rounded-bl-none first-child_tw-rounded-br-2xl first-child_tw-rounded-tl-2xl first-child_tw-rounded-tr-none first-child_tw-px-4 first-child_tw-py-2',
  ]);

  if (shouldRenderCenturionNetworkBadge) {
    return (
      <Badge
        size="large"
        status="neutral"
        type="text"
        // add custom colors to the badge for centurion network lounges (status/color not available in DLS component)
        className={`${badgeClassNames} first-child_tw-border-blue-deep-700 first-child_tw-bg-blue-deep-700 first-child_tw-text-blue-bright-100`}
        aria-hidden={true}
      >
        {centurionNetworkLabel}
      </Badge>
    );
  }

  if (isPriorityPass && enrollmentStatus !== enrollmentStatuses.ENROLLED) {
    return (
      <Badge
        size="large"
        status={priorityPassBadgeStatus}
        type="text"
        className={badgeClassNames}
        aria-hidden={true}
      >
        {priorityPassContent?.title}
      </Badge>
    );
  }

  return null;
};

export default LoungeTypeBadge;
